import classNames from 'classnames/bind';
import PropTypes from 'prop-types';

import React from 'react';
import {
  Form, Input, Textarea, CheckboxGroup,
  Checkbox, RadioGroup, Select
} from 'formsy-react-components';

import {
  Box, BoxContent, Toolbar, Button, TabBar,
  Icon, Grid, Column, Image, Footer, Alert,
  AdminInput, AdminTextArea, AdminTagsInput,
  AdminButton, AdminSecureInput, AdminImageInput,
  AdminTimeInput, AdminUrlInput, AdminSelect, AdminForm
} from '../components';

import { Http, merge, i18n } from 'common';
import moment from 'moment';

const OFFLINE_SCOPE_NAME = 'offline_access';

const sanitizeEnum = val => val !== '' ? val : undefined;

export default class ClientEdit extends AdminForm {
  config = {
    url: 'api/clients/',
    backUrl: '/'
  }

  cryptoFields = [
    'requestObjectSigningAlg',
    'userInfoSignedResponseAlg',
    'userInfoEncryptedResponseAlg',
    'userInfoEncryptedResponseEnc',
    'idTokenSignedResponseAlg',
    'idTokenEncryptedResponseAlg',
    'idTokenEncryptedResponseEnc',
    'codeChallengeMethod'
  ];

  componentWillMount() {
    this.setState({ currentTab: 'main' });
  }

  onCreateInitialObject = (initialObject) => {
    const { config } = this.context;

    initialObject.accessTokenTimeoutEnable = true;
    initialObject.requireAuthTime = true;

    initialObject.scope =
      Object.entries(config.systemScopes)
        .filter(i => i[1].defaultScope === true)
        .map(i => i[0]);
  }

  onCompleteLoad = (initialObject) => {
    if (initialObject.requireAuthTime == null)
      initialObject.requireAuthTime = true;

    initialObject.accessTokenTimeoutEnable = true;

    initialObject.allowRefresh =
      initialObject.grantTypes && initialObject.grantTypes.includes('refresh_token');

    initialObject.refreshTokenTimeoutEnable =
      initialObject.refreshTokenValiditySeconds != null;

    this.setState({allowRefresh: initialObject.allowRefresh})
  }

  onBeforeSubmit = (data) => {
    const allowRefresh = data.scope && data.scope.includes(OFFLINE_SCOPE_NAME);

    if (!allowRefresh || !data.refreshTokenTimeoutEnable)
      data.refreshTokenValiditySeconds = null;

    if (data.clientSecret != null)
      data.generateClientSecret = false;

    if (!data.clientSecret)
      data.clientSecret = undefined;

    if (allowRefresh && !data.grantTypes.includes('refresh_token'))
      data.grantTypes = [...data.grantTypes, 'refresh_token'];

    if (!allowRefresh && data.grantTypes.includes('refresh_token'))
      data.grantTypes = data.grantTypes.filter(i => i !== 'refresh_token');

    for (const f of this.cryptoFields) {
      data[f] = sanitizeEnum(data[f]);
    }
  }

  onAfterSubmit = (data) =>
    ` ID: ${data.clientId} Secret: ${data.clientSecret}`

  onTabChange = (value) => {
    this.setState({currentTab: value});
  }

  onScopeChange = (items) => {
    this.setState((prevState) => ({
      allowRefresh: items.includes(OFFLINE_SCOPE_NAME),
      initialObject: {
        ...prevState.initialObject,
        reuseRefreshToken: items.includes(OFFLINE_SCOPE_NAME),
        clearAccessTokensOnRefresh: items.includes(OFFLINE_SCOPE_NAME),
        refreshTokenTimeoutEnable: items.includes(OFFLINE_SCOPE_NAME)
      }
    }))
  }

  render() {
    const { config } = this.context;
    const { initialObject, allowRefresh } = this.state;

    const currentTab = this.state.currentTab;
    const systemScopes = merge(Object.keys(config.systemScopes), initialObject.scope);


    return (
      <div>
        <TabBar buttons={[['main', i18n('client-form.tab.main')], ['access', i18n('client-form.tab.access')], ['credentials', i18n('client-form.tab.credentials')], ['token', i18n('client-form.tab.token')], ['crypto', i18n('client-form.tab.crypto')], ['other', i18n('client-form.tab.other')]]}
                onChange={this.onTabChange} />

        <Box>
          <Form ref="form" onValidSubmit={this.onSubmit} onInvalidSubmit={this.onInvalid}>
            <Input name="createdAt" type="hidden" value={initialObject.createdAt||moment().format()} />
            <MainTab className={classNames({'hidden': currentTab !== 'main'})} initialObject={initialObject} />
            <AccessTab className={classNames({'hidden': currentTab !== 'access'})} scopes={systemScopes} initialObject={initialObject} onScopeChange={this.onScopeChange} />
            <CredentialsTab className={classNames({'hidden': currentTab !== 'credentials'})} initialObject={initialObject} />
            <TokensTab className={classNames({'hidden': currentTab !== 'token'})} initialObject={initialObject} allowRefresh={allowRefresh} />
            <CryptoTab className={classNames({'hidden': currentTab !== 'crypto'})} initialObject={initialObject} features={config.features} />
            <OtherTab className={classNames({'hidden': currentTab !== 'other'})} initialObject={initialObject} />

            <Footer className="txtcenter">
              <div className="mt3 mb3">
                <AdminButton type="submit" className="mr2" />
                <AdminButton type="cancel" onClick={this.onCancel} />
              </div>
            </Footer>
          </Form>
        </Box>
      </div>
    );
  }
};


const MainTab = ({initialObject, ...rest}) => (
  <div {...rest}>
    <BoxContent>
      <Grid columns="2">
        <Column>
          <AdminInput name="clientName" label={i18n('client-form.fields.client-name')} placeholder={i18n('client-form.hints.client-name')} value={initialObject.clientName} validations="isExistyVar" validationError="Name is required and can only contain latin letters and numbers" showErrors />
          <AdminInput name="clientId" label={i18n('client-form.fields.client-id')} placeholder={i18n('client-form.hints.client-id')} value={initialObject.clientId} validations="isVar" validationError="Can only contain latin letters and numbers" />
          <AdminTextArea name="clientDescription" label={i18n('client-form.fields.description')} placeholder={i18n('client-form.hints.description')} value={initialObject.clientDescription||''} />

          <AdminImageInput name="logoUri" label={i18n('client-form.fields.logo-uri')} placeholder={i18n('client-form.hints.logo-uri')} value={initialObject.logoUri} />
        </Column>

        <Column>
          <AdminUrlInput name="tosUri" label={i18n('client-form.fields.tos-uri')} placeholder={i18n('client-form.hints.tos-uri')} value={initialObject.tosUri||''} />
          <AdminUrlInput name="policyUri" label={i18n('client-form.fields.policy-uri')} placeholder={i18n('client-form.hints.policy-uri')} value={initialObject.policyUri||''} />
          <AdminUrlInput name="clientUri" label={i18n('client-form.fields.client-uri')} placeholder={i18n('client-form.hints.client-uri')} value={initialObject.clientUri||''} />
          <AdminInput name="softwareId" label={i18n('client-form.fields.software-id')} placeholder={i18n('client-form.hints.software-id')} value={initialObject.softwareId||''} />
          <AdminInput name="softwareVersion" label={i18n('client-form.fields.software-version')} placeholder={i18n('client-form.hints.software-version')} value={initialObject.softwareVersion||''} />
        </Column>
      </Grid>
    </BoxContent>

    <hr />

    <BoxContent>
      <AdminTagsInput name="redirectUris" title={i18n('client-form.fields.redirect-uris')} placeholder="http://" editMode="list"
                      hint={i18n('client-form.hints.redirect-uris')}
                      value={initialObject.redirectUris} newItemValidations="isUrl" />
    </BoxContent>

    <hr />

    <BoxContent>
      <AdminTagsInput name="contacts" title={i18n('client-form.fields.contacts')} placeholder="New Contact"
                      hint={i18n('client-form.hints.contacts')}
                      value={initialObject.contacts} editMode="list" />

    </BoxContent>

    <hr />

    <BoxContent>
      <AdminTagsInput name="resourceIds" title={i18n('client-form.fields.resources')} placeholder="New Resource"
                      hint={i18n('client-form.hints.resources')}
                      value={initialObject.resourceIds} editMode="list" />
    </BoxContent>
  </div>
)

class AccessTab extends React.Component {
  GRANT_TYPES = [
    {label: i18n('client-form.grant-types.authorization-code'), value: 'authorization_code'},
    {label: i18n('client-form.grant-types.client-credentials'), value: 'client_credentials'},
    {label: i18n('client-form.grant-types.password'), value: 'password'},
    {label: i18n('client-form.grant-types.implicit'), value: 'implicit'},
    {label: i18n('client-form.grant-types.redelegate'), value: 'urn:ietf:params:oauth:grant_type:redelegate'},
    {label: i18n('client-form.grant-types.device_code'), value: 'urn:ietf:params:oauth:grant-type:device_code'},
  ];

  SUBJECT_TYPE = [
    {label: i18n('client-form.subject-types.public'), value: 'PUBLIC'},
    {label: i18n('client-form.subject-types.pairwise'), value: 'PAIRWISE'}
  ];

  LOGIN_OPTIONS = [
    {label: i18n('client-form.login-options.email'), value: 'email'},
    {label: i18n('client-form.login-options.phone'), value: 'phone'},
    {label: i18n('client-form.login-options.facebook'), value: 'facebook'},
    {label: i18n('client-form.login-options.privatbankid'), value: 'pbbankid'},
    {label: i18n('client-form.login-options.nbubankid'), value: 'nbubankid'},
    {label: i18n('client-form.login-options.google'), value: 'google'},
    {label: i18n('client-form.login-options.linkedin'), value: 'linkedin'},
    {label: i18n('client-form.login-options.ecp'), value: 'eds'},
    {label: i18n('client-form.login-options.ecp-legal-entity'), value: 'enterprise'},
    {label: i18n('client-form.login-options.vodafone'), value: 'vodafone'},
    {label: i18n('client-form.login-options.kyivstar'), value: 'kyivstar'}
  ]

  render() {
    const {scopes, initialObject, onScopeChange, ...rest} = this.props;

    return (
      <div {...rest}>
        <BoxContent>
          <AdminTagsInput ref="scope" name="scope" title={i18n('client-form.fields.scope')} placeholder="New Scope"
                          hint={i18n('client-form.hints.scope')} newItemValidations="isScope"
                          presets={scopes} value={initialObject.scope} onChange={onScopeChange}  />
        </BoxContent>

        <hr />


        <BoxContent>
          <Grid columns="2">
            <Column>
              <CheckboxGroup name="grantTypes" label={i18n('client-form.fields.grant-types')}
                             options={this.GRANT_TYPES} value={initialObject.grantTypes||['authorization_code']} />
            </Column>

            <Column>
              <Checkbox name="allowIntrospection" label={i18n('client-form.fields.allow-introspection')} valueLabel={i18n('client-form.hints.allow-introspection')} />

              <div className="mt3">
                <RadioGroup name="subjectType" type="inline" label={i18n('client-form.fields.subject-type')}
                            options={this.SUBJECT_TYPE} value={initialObject.subjectType||'PUBLIC'} />
              </div>

              <div className="mt3">
                <AdminInput name="sectorIdentifierUri" label={i18n('client-form.fields.sector-identifier-uri')}
                            placeholder={i18n('client-form.hints.sector-identifier-uri')} validations="isUrl" />
              </div>
            </Column>
          </Grid>
        </BoxContent>

        <hr />

        <BoxContent>
          <CheckboxGroup name="loginOptionTypes" label={i18n('client-form.fields.login-options')}
                         options={this.LOGIN_OPTIONS} value={initialObject.loginOptionTypes||this.LOGIN_OPTIONS.map(i => i.value)} />
        </BoxContent>

      </div>
    );
  }
};

const CredentialsTab = ({ className, initialObject, ...rest }) => {
  const TOKEN_ENDPOINT_METHODS = [
    {label: i18n('client-form.token-endpoint-methods.secret-basic'), value: 'SECRET_BASIC'},
    {label: i18n('client-form.token-endpoint-methods.secret-post'), value: 'SECRET_POST'},
    {label: i18n('client-form.token-endpoint-methods.secret-jwt'), value: 'SECRET_JWT'},
    {label: i18n('client-form.token-endpoint-methods.private-key'), value: 'PRIVATE_KEY'},
    {label: i18n('client-form.token-endpoint-methods.none'), value: 'NONE'},
  ];

  const PUBLIC_KEY_SETS = [
    {label: i18n('client-form.public-key-sets.uri'), value: 'URI'},
    {label: i18n('client-form.public-key-sets.val'), value: 'VAL'},
  ];

  return (
    <div {...rest} className={classNames(className, 'mt3')} >
      <BoxContent>
        <RadioGroup name="tokenEndpointAuthMethod" label={i18n('client-form.fields.token-endpoint-auth-method')}
                    options={TOKEN_ENDPOINT_METHODS} value={initialObject.tokenEndpointAuthMethod||'SECRET_BASIC'} />

        <div className="mt3">
          <Grid columns="4">
            <Column>
              <strong>{i18n('client-form.fields.client-secret')}</strong>
            </Column>

            <Column>
              <AdminSecureInput name="clientSecret" placeholder=""
                                value={initialObject.clientSecret} layout="elementOnly"
                                autoComplete="none" />
              <p>{i18n('client-form.hints.client-secret')}</p>
            </Column>
          </Grid>
        </div>

        <div className="mt3">
          <RadioGroup name="jwksType" type="inline" label={i18n('client-form.fields.jwks-type')}
                      options={PUBLIC_KEY_SETS} value={initialObject.jwksType||'URI'} />


          <Grid columns="4">
            <Column></Column>
            <Column>
              <Input name="jwksUri" label={i18n('client-form.fields.jwks-uri')}
                     placeholder={i18n('client-form.hints.jwks-uri')}
                     layout="elementOnly" />
            </Column>
          </Grid>
        </div>

      </BoxContent>
    </div>);
}

class TokensTab extends React.Component {
  state = {
    refreshTokenTimeoutEnable: false
  }

  componentWillReceiveProps(props) {
    const { initialObject, allowRefresh } = props;

    const refreshTokenEnable =
      allowRefresh != null ?
        allowRefresh :
        initialObject.allowRefresh;

    const refreshTokenTimeoutEnable = initialObject.hasOwnProperty('refreshTokenTimeoutEnable') ?
      initialObject.refreshTokenTimeoutEnable : !!initialObject.refreshTokenValiditySeconds

    this.setState({
      refreshTokenEnable,
      refreshTokenTimeoutEnable
    });
  }

  render() {
    const {
      initialObject,
      allowRefresh,
      ...rest
    } = this.props;

    const {
      refreshTokenEnable,
      refreshTokenTimeoutEnable
    } = this.state;

    return (
      <div {...rest}>
        <BoxContent>
          <div className="w90 clearfix ml-checkbox">
            <AdminTimeInput name="accessTokenValiditySeconds" title={i18n('client-form.fields.access-token-validity-seconds')}
                            value={initialObject.accessTokenValiditySeconds||600}
                            validations='isPositiveInt' validationError={i18n('error.non-integer')} />
          </div>

          <div className="mt1 w90 ml-checkbox">
            <AdminTimeInput name="idTokenValiditySeconds" title={i18n('client-form.fields.id-token-validity-seconds')}
                            value={initialObject.idTokenValiditySeconds||600}
                            validations='isPositiveInt' validationError={i18n('error.non-integer')} />
          </div>

          <div className="mt1 w90 ml-checkbox">
            <AdminTimeInput name="deviceCodeValiditySeconds" title={i18n('client-form.fields.device-code-validity-seconds')}
                            value={initialObject.deviceCodeValiditySeconds||1800}
                            validations='isPositiveInt' validationError={i18n('error.non-integer')} />
          </div>

          <hr className="mt3 mb3" />

          <div>
            <div>
              <Alert text={i18n('client-form.hints.allow-refresh')}
                     type={allowRefresh ? 'success' : 'info'} />

              <Checkbox name="allowRefresh" valueLabel={i18n('client-form.fields.allow-refresh')}
                        layout="elementOnly" value={allowRefresh} disabled />

              <Checkbox ref="reuseRefreshToken" name="reuseRefreshToken" valueLabel={i18n('client-form.fields.reuse-refresh-token')}
                        layout="elementOnly" value={initialObject.reuseRefreshToken} disabled={!refreshTokenEnable} />

              <Checkbox ref="clearAccessTokensOnRefresh" name="clearAccessTokensOnRefresh" valueLabel={i18n('client-form.fields.clear-access-tokens-on-refresh')}
                        layout="elementOnly" value={initialObject.clearAccessTokensOnRefresh} disabled={!refreshTokenEnable} />
            </div>

            <div className="clearfix mt3">
              <div className="left mr-checkbox">
                <Checkbox name="refreshTokenTimeoutEnable" value={refreshTokenTimeoutEnable} layout="elementOnly"
                          disabled={!refreshTokenEnable} onChange={(n, v) => this.setState({refreshTokenTimeoutEnable: v})} />
              </div>

              <div className="left w90">
                <AdminTimeInput name="refreshTokenValiditySeconds" title={i18n('client-form.fields.refresh-token-validity-seconds')}
                                disabled={!(refreshTokenEnable&&refreshTokenTimeoutEnable)}
                                value={initialObject.refreshTokenValiditySeconds||86400} />
              </div>
            </div>

          </div>

        </BoxContent>
      </div>
    );
  }
};

const CryptoTab = ({initialObject, features, ...rest}) => {
  const SIGNING_ALGORIGHM = [
    {label: i18n('client-form.signing-algorighm.default'), value: ''},
    {label: i18n('client-form.signing-algorighm.HS256'), value: 'HS256'},
    {label: i18n('client-form.signing-algorighm.HS384'), value: 'HS384'},
    {label: i18n('client-form.signing-algorighm.HS512'), value: 'HS512'},
    {label: i18n('client-form.signing-algorighm.RS256'), value: 'RS256'},
    {label: i18n('client-form.signing-algorighm.RS384'), value: 'RS384'},
    {label: i18n('client-form.signing-algorighm.RS512'), value: 'RS512'},
    {label: i18n('client-form.signing-algorighm.ES256'), value: 'ES256'},
    {label: i18n('client-form.signing-algorighm.ES384'), value: 'ES384'},
    {label: i18n('client-form.signing-algorighm.ES512'), value: 'ES512'},
    {label: i18n('client-form.signing-algorighm.PS256'), value: 'PS256'},
    {label: i18n('client-form.signing-algorighm.PS384'), value: 'PS384'},
    {label: i18n('client-form.signing-algorighm.PS512'), value: 'PS512'},
  ];

  const ENCRYPTION_ALGORITHM = [
    {label: i18n('client-form.encryption-algorithm.default'), value: ''},
    {label: i18n('client-form.encryption-algorithm.RSA-OAEP'), value: 'RSA-OAEP'},
    {label: i18n('client-form.encryption-algorithm.RSA-OAEP-256'), value: 'RSA-OAEP-256'},
    {label: i18n('client-form.encryption-algorithm.RSA1_5'), value: 'RSA1_5'},
  ];

  const ENCRYPTION_METHOD = [
    {label: i18n('client-form.encryption-method.default'), value: ''},
    {label: i18n('client-form.encryption-method.A128CBC+HS256'), value: 'A128CBC+HS256'},
    {label: i18n('client-form.encryption-method.A256CBC+HS512'), value: 'A256CBC+HS512'},
    {label: i18n('client-form.encryption-method.A256GCM'), value: 'A256GCM'},
    {label: i18n('client-form.encryption-method.A192GCM'), value: 'A192GCM'},
    {label: i18n('client-form.encryption-method.A128GCM'), value: 'A128GCM'},
    {label: i18n('client-form.encryption-method.A128CBC-HS256'), value: 'A128CBC-HS256'},
    {label: i18n('client-form.encryption-method.A192CBC-HS384'), value: 'A192CBC-HS384'},
    {label: i18n('client-form.encryption-method.A256CBC-HS512'), value: 'A256CBC-HS512'},
  ];

  const CODE_CHALLENGE_METHOD = [
    {label: i18n('client-form.code-challenge-method.default'), value: ''},
    {label: i18n('client-form.code-challenge-method.plain'), value: 'plain'},
    {label: i18n('client-form.code-challenge-method.S256'), value: 'S256'},
  ];

  const TOKEN_SIGNING_ALGORIGHM = [
    ...SIGNING_ALGORIGHM,
    {label: i18n('client-form.signing-algorighm.none'), value: 'none'}
  ];

  const supports = (items, features) =>
    items.filter(i => ['default', 'none', ''].includes(i.value) || features.includes(i.value));

  return (
    <div {...rest}>
      <BoxContent>
        <Grid columns="2">
          <Column>
            <AdminSelect name="requestObjectSigningAlg" label={i18n('client-form.fields.request-object-signing-alg')}
                         options={supports(SIGNING_ALGORIGHM, features.request_object_signing_alg_values_supported)}
                         value={initialObject.requestObjectSigningAlg||''} />

            <AdminSelect name="userInfoSignedResponseAlg" label={i18n('client-form.fields.user-info-signed-response-alg')}
                         options={supports(SIGNING_ALGORIGHM, features.userinfo_signing_alg_values_supported)}
                         value={initialObject.userInfoSignedResponseAlg||''} />

            <AdminSelect name="userInfoEncryptedResponseAlg" label={i18n('client-form.fields.user-info-encrypted-response-alg')}
                         options={supports(ENCRYPTION_ALGORITHM, features.userinfo_encryption_alg_values_supported)}
                         value={initialObject.userInfoEncryptedResponseAlg||''} />

            <AdminSelect name="userInfoEncryptedResponseEnc" label={i18n('client-form.fields.user-info-encrypted-response-enc')}
                         options={supports(ENCRYPTION_METHOD, features.userinfo_encryption_enc_values_supported)}
                         value={initialObject.userInfoEncryptedResponseEnc||''} />
          </Column>

          <Column>
            <AdminSelect name="idTokenSignedResponseAlg" label={i18n('client-form.fields.id-token-signed-response-alg')}
                         options={supports(TOKEN_SIGNING_ALGORIGHM, features.id_token_signing_alg_values_supported)}
                         value={initialObject.idTokenSignedResponseAlg||''} />

            <AdminSelect name="idTokenEncryptedResponseAlg" label={i18n('client-form.fields.id-token-encrypted-response-alg')}
                         options={supports(ENCRYPTION_ALGORITHM, features.id_token_encryption_alg_values_supported)}
                         value={initialObject.idTokenEncryptedResponseAlg||''} />

            <AdminSelect name="idTokenEncryptedResponseEnc" label={i18n('client-form.fields.id-token-encrypted-response-enc')}
                         options={supports(ENCRYPTION_METHOD, features.id_token_encryption_enc_values_supported)}
                         value={initialObject.idTokenEncryptedResponseEnc||''} />

            <AdminSelect name="codeChallengeMethod" label={i18n('client-form.fields.code-challenge-method')}
                         options={supports(CODE_CHALLENGE_METHOD, features.code_challenge_methods_supported)}
                         value={initialObject.codeChallengeMethod||''} />
          </Column>
        </Grid>

      </BoxContent>
    </div>
  );
}

const OtherTab = ({initialObject, ...rest}) => (
  <div {...rest}>
    <BoxContent>
      <AdminTagsInput name="claimsRedirectUris" title={i18n('client-form.fields.claims-redirect-uris')}
                      placeholder="https://" newItemValidations="isUrl"
                      hint={i18n('client-form.hints.claims-redirect-uris')}
                      value={initialObject.claimsRedirectUris||[]} editMode="list" />

      <hr />

      <div className="mt3">
        <Checkbox name="requireAuthTime" label={i18n('client-form.fields.require-auth-time')}
                  valueLabel={i18n('client-form.hints.require-auth-time')}
                  value={initialObject.requireAuthTime} />
      </div>

    </BoxContent>
  </div>
);
